import React, { useState, useMemo } from 'react';
import { GraduationCap, SlidersHorizontal, CheckCircle2, AlertTriangle, ArrowUpDown, ArrowUp, ArrowDown, RefreshCw } from 'lucide-react';
import SelectFiltre from '../shared/SelectFiltre';
import BarreRecherche from '../shared/BarreRecherche';

// FilterChip component - responsive
const FilterChip = ({ label, active, onClick, count, color }) => (
  <button
    onClick={onClick}
    className={`
      px-2.5 sm:px-4 xl:px-3 2xl:px-3.5 3xl:px-4 py-1.5 sm:py-2 xl:py-1.5 2xl:py-1.5 3xl:py-2 rounded-lg sm:rounded-xl font-medium text-xs sm:text-sm xl:text-xs 2xl:text-xs 3xl:text-sm transition-all whitespace-nowrap
      ${active
        ? `${color} text-white shadow-lg`
        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
      }
    `}
  >
    {label}
    {count !== undefined && (
      <span className={`ml-1 sm:ml-2 ${active ? 'opacity-90' : 'opacity-60'}`}>
        ({count})
      </span>
    )}
  </button>
);

const FiltreClasse = ({
  classes = [],
  onFilterChange,
}) => {
  // États des filtres
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedNiveaux, setSelectedNiveaux] = useState([]);
  const [selectedStatut, setSelectedStatut] = useState('all');
  const [sortBy, setSortBy] = useState('nom');
  const [sortOrder, setSortOrder] = useState('asc');

  // Calculer les counts par niveau
  const niveauCounts = useMemo(() => {
    const counts = { '6e': 0, '5e': 0, '4e': 0, '3e': 0 };
    classes.forEach(classe => {
      // Pour les groupes, utiliser le premier caractère de la première classe d'origine
      const niveau = classe.classes_detail && classe.classes_detail.length > 0
        ? classe.classes_detail[0].charAt(0)
        : classe.nom?.charAt(0);
      if (niveau && counts[`${niveau}e`] !== undefined) {
        counts[`${niveau}e`]++;
      }
    });
    return counts;
  }, [classes]);

  const sortOptions = [
    { value: 'nom', label: 'Nom' },
    { value: 'moyenne', label: 'Moyenne' },
    { value: 'eleves', label: 'Nb élèves' },
  ];

  // Toggle niveau
  const toggleNiveau = (niveau) => {
    setSelectedNiveaux(prev =>
      prev.includes(niveau)
        ? prev.filter(n => n !== niveau)
        : [...prev, niveau]
    );
  };

  // Vérifier si des filtres sont actifs
  const hasActiveFilters = useMemo(() => {
    return searchQuery !== ''
      || selectedNiveaux.length > 0
      || selectedStatut !== 'all'
      || sortBy !== 'nom'
      || sortOrder !== 'asc';
  }, [searchQuery, selectedNiveaux, selectedStatut, sortBy, sortOrder]);

  // Réinitialiser les filtres
  const resetFilters = () => {
    setSearchQuery('');
    setSelectedNiveaux([]);
    setSelectedStatut('all');
    setSortBy('nom');
    setSortOrder('asc');
  };

  // Filtrer et trier les classes
  const filteredClasses = useMemo(() => {
    let filtered = [...classes];

    // Recherche
    if (searchQuery) {
      filtered = filtered.filter(classe =>
        classe.nom?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Niveau
    if (selectedNiveaux.length > 0) {
      filtered = filtered.filter(classe => {
        // Pour les groupes, utiliser le premier caractère de la première classe d'origine
        const niveau = classe.classes_detail && classe.classes_detail.length > 0
          ? classe.classes_detail[0].charAt(0)
          : classe.nom?.charAt(0);
        return selectedNiveaux.includes(`${niveau}e`);
      });
    }

    // Statut
    if (selectedStatut === 'active') {
      filtered = filtered.filter(classe => {
        const nbEleves = classe.nombre_eleves ?? classe.nbeleves ?? 0;
        return nbEleves > 0;
      });
    } else if (selectedStatut === 'vide') {
      filtered = filtered.filter(classe => {
        const nbEleves = classe.nombre_eleves ?? classe.nbeleves ?? 0;
        return nbEleves === 0;
      });
    }


    // Tri
    filtered.sort((a, b) => {
      let compareValue = 0;

      switch (sortBy) {
        case 'nom':
          compareValue = (a.nom || '').localeCompare(b.nom || '');
          break;
        case 'moyenne':
          compareValue = (a.moyenne_classe || 0) - (b.moyenne_classe || 0);
          break;
        case 'eleves':
          compareValue = (a.nombre_eleves || 0) - (b.nombre_eleves || 0);
          break;
        default:
          compareValue = 0;
      }

      return sortOrder === 'asc' ? compareValue : -compareValue;
    });

    return filtered;
  }, [classes, searchQuery, selectedNiveaux, selectedStatut, sortBy, sortOrder]);

  // Notifier le parent des changements
  React.useEffect(() => {
    if (onFilterChange) {
      onFilterChange(filteredClasses);
    }
  }, [filteredClasses, onFilterChange]);

  return (
    <div className="mb-4 sm:mb-8 xl:mb-5 2xl:mb-6 3xl:mb-8">
      {/* Filters section */}
      <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg shadow-slate-200/50 border border-slate-100 p-3 sm:p-5 xl:p-3 2xl:p-4 3xl:p-5 mb-4 sm:mb-6 xl:mb-4 2xl:mb-5 3xl:mb-6">
        <div className="flex flex-col gap-3 sm:gap-5 xl:gap-3 2xl:gap-4 3xl:gap-5">
          {/* Row 1: Search */}
          <BarreRecherche
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Rechercher une classe..."
            className="w-full"
          />

          {/* Row 2: Niveau filters - scrollable on mobile */}
          <div className="flex items-center gap-2 sm:gap-3 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-hide">
            <span className="text-xs sm:text-sm font-medium text-slate-500 flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
              <GraduationCap size={14} className="sm:w-4 sm:h-4" />
              <span className="hidden xs:inline">Niveau :</span>
            </span>
            <div className="flex gap-1.5 sm:gap-2">
              <FilterChip
                label="6ème"
                active={selectedNiveaux.includes('6e')}
                onClick={() => toggleNiveau('6e')}
                count={niveauCounts['6e']}
                color="bg-blue-500"
              />
              <FilterChip
                label="5ème"
                active={selectedNiveaux.includes('5e')}
                onClick={() => toggleNiveau('5e')}
                count={niveauCounts['5e']}
                color="bg-purple-500"
              />
              <FilterChip
                label="4ème"
                active={selectedNiveaux.includes('4e')}
                onClick={() => toggleNiveau('4e')}
                count={niveauCounts['4e']}
                color="bg-emerald-500"
              />
              <FilterChip
                label="3ème"
                active={selectedNiveaux.includes('3e')}
                onClick={() => toggleNiveau('3e')}
                count={niveauCounts['3e']}
                color="bg-rose-500"
              />
            </div>
          </div>

          {/* Row 3: Status + Sort - responsive layout */}
          <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2 sm:gap-3">
            {/* Status label - mobile only shows icon */}
            <span className="text-xs sm:text-sm font-medium text-slate-500 flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
              <SlidersHorizontal size={14} className="sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Filtres :</span>
            </span>

            {/* Statut filter - compact on mobile */}
            <div className="flex items-center gap-0.5 sm:gap-1 p-0.5 sm:p-1 bg-slate-100 rounded-lg sm:rounded-xl overflow-x-auto">
              <button
                onClick={() => setSelectedStatut('all')}
                className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-md sm:rounded-lg text-xs sm:text-sm font-medium transition-all whitespace-nowrap ${selectedStatut === 'all' ? 'bg-white shadow text-slate-800' : 'text-slate-500 hover:text-slate-700'}`}
              >
                Toutes
              </button>
              <button
                onClick={() => setSelectedStatut('active')}
                className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-md sm:rounded-lg text-xs sm:text-sm font-medium transition-all flex items-center gap-1 sm:gap-1.5 whitespace-nowrap ${selectedStatut === 'active' ? 'bg-white shadow text-emerald-600' : 'text-slate-500 hover:text-slate-700'}`}
              >
                <CheckCircle2 size={12} className="sm:w-3.5 sm:h-3.5" />
                <span className="hidden xs:inline">Actives</span>
              </button>
              <button
                onClick={() => setSelectedStatut('vide')}
                className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-md sm:rounded-lg text-xs sm:text-sm font-medium transition-all flex items-center gap-1 sm:gap-1.5 whitespace-nowrap ${selectedStatut === 'vide' ? 'bg-white shadow text-orange-600' : 'text-slate-500 hover:text-slate-700'}`}
              >
                <AlertTriangle size={12} className="sm:w-3.5 sm:h-3.5" />
                <span className="hidden xs:inline">Vides</span>
              </button>
            </div>

            {/* Separator - hidden on mobile */}
            <div className="w-px h-6 bg-slate-200 hidden md:block" />

            {/* Sort controls */}
            <div className="flex items-center gap-1.5 sm:gap-2">
              {/* Sort dropdown */}
              <SelectFiltre
                label="Trier"
                value={sortOptions.find(o => o.value === sortBy)?.label || 'Nom'}
                options={sortOptions}
                onChange={setSortBy}
                icon={ArrowUpDown}
              />

              {/* Sort order */}
              <button
                onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                className="p-2 sm:p-2.5 bg-white border border-slate-200 rounded-lg sm:rounded-xl hover:border-slate-300 transition-colors flex-shrink-0"
                title={sortOrder === 'asc' ? 'Croissant' : 'Décroissant'}
              >
                {sortOrder === 'asc' ? (
                  <ArrowUp size={16} className="text-slate-600" />
                ) : (
                  <ArrowDown size={16} className="text-slate-600" />
                )}
              </button>

              {/* Reset filters - adapts to available space */}
              {hasActiveFilters && (
                <button
                  onClick={resetFilters}
                  className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg sm:rounded-xl transition-colors ml-auto"
                >
                  <RefreshCw size={12} className="sm:w-3.5 sm:h-3.5" />
                  <span className="hidden sm:inline">Réinitialiser</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Results count */}
      <div className="flex items-center justify-between mb-4 sm:mb-6 xl:mb-3 2xl:mb-4 3xl:mb-6 px-1 sm:px-2">
        <p className="text-sm sm:text-base xl:text-xs 2xl:text-sm 3xl:text-base text-slate-500">
          <span className="font-semibold text-slate-700">{filteredClasses.length}</span> classe(s)
          <span className="hidden sm:inline"> trouvée(s)</span>
          {hasActiveFilters && <span className="hidden sm:inline"> avec les filtres actifs</span>}
        </p>
      </div>
    </div>
  );
};

export default FiltreClasse;
